/*
 * Powerlete collection filters — desktop "push & reflow" toggle.
 *
 * Horizon's native VERTICAL facets already lay the filter pane and the product
 * grid out in a single CSS grid. This component adds a desktop toggle that
 * opens/closes the pane on demand, driving the push/reflow via a `pw-open` class
 * on the parent <results-list>. The CSS keeps the grid full width and animates its
 * inline-start padding (matching the live av-filter slide). All the actual
 * filtering (AJAX, URL sync, facet form) stays 100% native.
 *
 * Scope: desktop only (>= 750px). Used by BOTH the collection and search results
 * sections (each loads this asset + renders the vertical pane); the shared
 * sessionStorage key `pwFiltersOpen` is reset to closed on every fresh load
 * (connectedCallback), so open-state never leaks between the two page types.
 * On mobile the native off-canvas drawer is used.
 *
 * Filter/sort applies morph this section. The morph resets the results-list class
 * to its server value (which has `pw-coll-toggle` but not the JS-added `pw-open`)
 * and does NOT re-run connectedCallback, so we:
 *   1. delegate the toggle click to the host element (survives the inner button
 *      being replaced by the morph), and
 *   2. re-assert `pw-open` from sessionStorage via a MutationObserver after each
 *      morph, so the open pane stays open while the shopper refines (matches live).
 *      MutationObserver callbacks run before paint, so there is no flicker.
 */

// sessionStorage can throw (private mode / storage disabled). These wrappers keep the pane
// toggle working (default = closed) instead of throwing out of a filter interaction.
const _pwFiltersGet = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const _pwFiltersSet = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    /* storage blocked: pane state simply won't persist across morphs */
  }
};

class PowerleteCollectionFilters extends HTMLElement {
  connectedCallback() {
    this.results = this.closest('results-list');
    if (!this.results) return;

    this.mq = window.matchMedia('(min-width: 750px)');
    this.results.classList.add('pw-coll-toggle');

    this._onToggle = this._onToggle.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onMqChange = this._onMqChange.bind(this);
    this._reassertOpen = this._reassertOpen.bind(this);

    // Delegate to the host so the toggle keeps working after a morph replaces the button.
    this.addEventListener('click', this._onToggle);
    document.addEventListener('keydown', this._onKeydown);
    this.mq.addEventListener('change', this._onMqChange);

    // Keep the pane open across section morphs (filter/sort apply).
    this._classObserver = new MutationObserver(this._reassertOpen);
    this._classObserver.observe(this.results, { attributes: true, attributeFilter: ['class'] });

    // Live parity: start CLOSED on every fresh page load (live does not persist the
    // open pane across navigations). Within a page, opening persists so the pane stays
    // open through filter/sort section morphs (see _reassertOpen); connectedCallback
    // does not re-run on a morph, so this reset only fires on a real navigation.
    _pwFiltersSet('pwFiltersOpen', '0');
    this._setOpen(false, false, false);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
    if (this.mq) this.mq.removeEventListener('change', this._onMqChange);
    if (this._classObserver) this._classObserver.disconnect();
  }

  get button() {
    return this.querySelector('.pw-filters__toggle');
  }

  get label() {
    return this.querySelector('.pw-filters__label');
  }

  _syncLabel(open) {
    const label = this.label;
    if (!label) return;
    const text = open ? this.dataset.labelHide : this.dataset.labelShow;
    if (text) label.textContent = text;
  }

  get isOpen() {
    return this.results.classList.contains('pw-open');
  }

  _onToggle(event) {
    if (!event.target.closest('.pw-filters__toggle')) return;
    this._setOpen(!this.isOpen, true, true);
  }

  _onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen && this.mq.matches) {
      this._setOpen(false, true, true);
      const btn = this.button;
      if (btn) btn.focus();
    }
  }

  _onMqChange(event) {
    // Dropping to mobile: hand back to the native drawer (close our pane state).
    if (!event.matches && this.isOpen) {
      this._setOpen(false, false, false);
    }
  }

  _reassertOpen() {
    if (!this.mq.matches) return;
    if (_pwFiltersGet('pwFiltersOpen') !== '1') return;
    if (this.results.classList.contains('pw-open')) return;
    this.results.classList.add('pw-open');
    const btn = this.button;
    if (btn) btn.setAttribute('aria-expanded', 'true');
    this._syncLabel(true);
  }

  _setOpen(open, persist, moveFocus) {
    this.results.classList.toggle('pw-open', open);
    const btn = this.button;
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    this._syncLabel(open);
    if (persist) _pwFiltersSet('pwFiltersOpen', open ? '1' : '0');

    if (open && moveFocus) {
      const pane = this.results.querySelector(
        '.facets-block-wrapper--vertical:not(#filters-drawer)'
      );
      if (pane) {
        const focusable = pane.querySelector(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])'
        );
        if (focusable) focusable.focus();
      }
    }
  }
}

if (!customElements.get('powerlete-collection-filters')) {
  customElements.define('powerlete-collection-filters', PowerleteCollectionFilters);
}
